import {useEffect, useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from '@mui/material';
import './DocListFilterAccordion.scss';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as ChevronIcon} from '../../../../assets/images/chevron-base-icon.svg';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {DocMetadataFilter} from '../../../../shared/interfaces';
import DocListParentFilter, {
  DocListParentFilterData,
} from '../doc-list-parent-filter/DocListParentFilter';

import ArrFilterChip from '../../../common/arr-filter-chip/ArrFilterChip';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import DocListFilterSelectionModal, {
  FILTER_SELECTION_MAP,
} from '../doc-list-filter-selection-modal/DocListFilterSelectionModal';
import {decodeHtmlEntities} from '../../../../utils/utils';

export type filterSelectionType =
  | 'include'
  | 'exclude'
  | 'before'
  | 'after'
  | 'on'
  | 'between';

type DocListFilterAccordionProps = {
  filters: DocMetadataFilter[];
  title: string;
  expandText?: string;
  collapseText?: string;
  tooltipText?: string;
  tooltipTitle?: string;
  expand?: boolean;
  onFilterChange: (param: any) => void;
  filterOptions?: Array<{
    text: string;
    value: string;
    disabled?: boolean;
    group?: string | null;
  }>;
  loadingOptions?: boolean;
  errorOptions?: boolean;
};
type FilterValue = {
  disabled: boolean;
  group: string | null;
  selected: boolean;
  text: string;
  value: string;
};
export type ActiveFilter = {
  id: string;
  type: 'NVARCHAR' | 'DATETIME' | 'INT';
  label: string;
  values?: FilterValue[];
  selectionType?: filterSelectionType | 'below' | 'over' | 'at' | 'between';
  seperator?: string;
  startDate?: string;
  endDate?: string;
  startSize?: string;
  endSize?: string;
  isSelected?: boolean;
};

const DocListFilterAccordion: React.FC<DocListFilterAccordionProps> = ({
  filters,
  title,
  tooltipTitle = '',
  expandText = '',
  collapseText = '',
  tooltipText = '',
  expand = true,
  onFilterChange, // eslint-disable-next-line @typescript-eslint/no-unused-vars
  filterOptions = [], // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loadingOptions = false, // eslint-disable-next-line @typescript-eslint/no-unused-vars
  errorOptions = false,
}) => {
  const [expanded, setExpanded] = useState(expand);

  const [parentFilters, setParentFilters] = useState<DocListParentFilterData[]>(
    []
  );

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [openDropdownFocusEl, setOpenDropdownFocusEl] =
    useState<HTMLElement | null>(null);

  const [filterModalState, setFilterModalState] = useState<{
    anchorEl: HTMLElement | null;
    selectedFilter: ActiveFilter | null;
  }>({anchorEl: null, selectedFilter: null});

  useEffect(() => {
    function moveFocusTocloseBtn(e: KeyboardEvent) {
      if (
        e.key === 'Tab' &&
        filterModalState.anchorEl &&
        !openDropdownFocusEl
      ) {
        e.preventDefault();
        (
          document.querySelector(
            '[data-testid="doc-list-filter-selection-close-button"]'
          ) as HTMLElement
        )?.focus();
        setOpenDropdownFocusEl(filterModalState.anchorEl);
      }
    }

    window.addEventListener('keydown', moveFocusTocloseBtn);
    return () => window.removeEventListener('keydown', moveFocusTocloseBtn);
  }, [filterModalState.anchorEl, openDropdownFocusEl]);

  // Keyboard navigation for accordion
  const handleAccordionKeyDown = (event: React.KeyboardEvent) => {
    // Only toggle accordion if Enter/Space is pressed on the summary itself
    // Don't intercept keys from child elements (like parent filter items)
    if (event.key === 'Enter' || event.key === ' ') {
      const target = event.target as HTMLElement;
      // Check if the event originated from the accordion summary button
      const isFromSummary =
        target.closest('.MuiAccordionSummary-root') ===
        event.currentTarget.querySelector('.MuiAccordionSummary-root');

      if (isFromSummary && !target.closest('.parent-filter-container')) {
        event.preventDefault();
        setExpanded((prev) => {
          modalCloseHandler();
          return !prev;
        });
      }
    }
    // Arrow keys for navigation between accordions (handled by parent)
  };

  const getFilterInfo = (
    id: string,
    filterType: 'parent' | 'active'
  ): ActiveFilter => {
    if (filterType == 'parent') {
      const parentFilter =
        parentFilters.find((filter) => filter.id == id) ??
        ({} as DocListParentFilterData);
      return {
        id: parentFilter.id,
        label: parentFilter.label,
        type: parentFilter.type,
        isSelected: parentFilter.isSelected,
      } as ActiveFilter;
    }
    return structuredClone(
      activeFilters.find((filter) => filter.id == id) ?? ({} as ActiveFilter)
    );
  };
  const updateParentFilter = (id: string, isSelected: boolean) => {
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: isSelected};
        }
        return filter;
      });
    });
  };
  const getActiveFiltersChip = () => {
    return activeFilters.map((filter) => {
      let chipContent;

      if (filter.type == 'NVARCHAR') {
        chipContent = (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? 'include'],
            ]}
            selections={
              filter.values?.map((value) => {
                return {id: value.text, value: `"${value.text}"`};
              }) ?? []
            }
            variant="v2"
            isResponsive={false}
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'DATETIME') {
        let dateSelection = [
          {id: filter.startDate ?? '', value: filter.startDate ?? ''},
        ];
        if (filter.endDate) {
          dateSelection = [
            ...dateSelection,
            {id: filter.endDate ?? '', value: filter.endDate ?? ''},
          ];
        }
        chipContent = (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? 'include'],
            ]}
            selections={[...dateSelection]}
            variant="v2"
            isResponsive={false}
            operator="to"
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'INT') {
        let sizeSelection = [
          {id: filter.startSize ?? '', value: filter.startSize ?? ''},
        ];
        if (filter.endSize) {
          sizeSelection = [
            ...sizeSelection,
            {id: filter.endSize ?? '', value: filter.endSize ?? ''},
          ];
        }
        chipContent = (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? 'between'],
            ]}
            selections={[...sizeSelection]}
            variant="v2"
            isResponsive={false}
            operator="to"
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      }

      return (
        <li
          key={filter.id}
          data-testid={`doc-list-filter-accordion-li-${filter.id}`}
        >
          {chipContent}
        </li>
      );
    });
  };

  const parentFilterClickHandler = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    filterData: {id: string; [key: string]: any}
  ) => {
    let targetElement: HTMLElement | null = null;

    if ('currentTarget' in event) {
      targetElement = event.currentTarget;
    }

    // Handle both click and Enter/Space key events
    if (
      event.type === 'click' ||
      ('key' in event && (event.key === 'Enter' || event.key === ' '))
    ) {
      if ('key' in event) {
        event.preventDefault();
      }

      if (filterModalState.selectedFilter?.id == filterData.id) return;
      const flterInfo: ActiveFilter = getFilterInfo(filterData.id, 'parent');
      if (flterInfo.isSelected) return;

      setFilterModalState({
        anchorEl: targetElement,
        selectedFilter: {...flterInfo},
      });
    }
  };
  const activeFilterClickHandler = (
    element: HTMLElement | null,
    id?: string
  ) => {
    if (element) {
      setFilterModalState({
        anchorEl: element,
        selectedFilter: {...getFilterInfo(id ?? '', 'active')},
      });
    }
  };
  const modalCloseHandler = () => {
    setFilterModalState({
      anchorEl: null,
      selectedFilter: null,
    });
    setTimeout(() => {
      openDropdownFocusEl?.focus();
    }, 100);
    setOpenDropdownFocusEl(null);
  };

  const transformFilterValuesForAPI = (values: any[] | undefined) => {
    return Array.isArray(values)
      ? values.map((v) => {
          if (typeof v === 'string') {
            const decoded = decodeHtmlEntities(v);
            let result = decoded;
            if (decoded.startsWith("'") && decoded.endsWith("'")) {
              result = `'${decoded}'`;
            }
            return result;
          } else if (v && typeof v === 'object' && v.value) {
            let val = v.value;
            let text = v.text;
            if (typeof val === 'string') {
              val = decodeHtmlEntities(val);
              if (val.startsWith("'") && val.endsWith("'")) {
                val = `'${val}'`;
              }
            }
            if (typeof text === 'string') {
              text = decodeHtmlEntities(text);
              if (text.startsWith("'") && text.endsWith("'")) {
                text = `'${text}'`;
              }
            }
            return {
              ...v,
              value: val,
              text: text,
            };
          }
          return v;
        })
      : values;
  };

  const onAddToFiltersHandler = (filterData: ActiveFilter) => {
    const transformedFilterData = {
      ...filterData,
      values: transformFilterValuesForAPI(filterData.values),
    };
    const normalizedFilterData: ActiveFilter = {
      ...transformedFilterData,
      values:
        transformedFilterData.type === 'NVARCHAR' &&
        Array.isArray(transformedFilterData.values)
          ? transformedFilterData.values.map((v: any) => {
              if (typeof v === 'string') {
                return {
                  id: v,
                  value: v,
                  text: v,
                  disabled: false,
                  group: null,
                  selected: true,
                };
              } else {
                return {
                  id: v.value || v.id || v.text,
                  value: v.value || v.label || v.text || '',
                  text: v.label || v.value || v.text || '',
                  disabled: v.disabled ?? false,
                  group: v.group ?? null,
                  selected: v.selected ?? true,
                };
              }
            })
          : transformedFilterData.values,
    };
    const existingActiveFilter = activeFilters.find(
      (filter) => filter.id == normalizedFilterData.id
    );
    let updatedActiveFilters = activeFilters.map((filter) =>
      filter.id == normalizedFilterData.id
        ? structuredClone(normalizedFilterData)
        : filter
    );
    if (!existingActiveFilter) {
      updatedActiveFilters = [
        ...updatedActiveFilters,
        structuredClone(normalizedFilterData),
      ];
    }
    setActiveFilters([...updatedActiveFilters]);
    updateParentFilter(normalizedFilterData.id, true);
    setActiveFilters([...updatedActiveFilters]);
    modalCloseHandler();
  };
  const clearAllHandler = () => {
    modalCloseHandler();
    setActiveFilters([]);
    setParentFilters((prev) => {
      return prev.map((filter) => {
        return {...filter, isSelected: false};
      });
    });
    // Notify parent component that filters have been cleared
    onFilterChange([]);
  };

  // Keyboard handler for Clear All button
  const handleClearAllKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      clearAllHandler();
    }
  };

  // Keyboard handler for Apply Filters button
  const handleApplyFiltersKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onFilterChange(activeFilters);
    }
  };
  const clearActiveFilterHandler = (id?: string) => {
    if (id == filterModalState.selectedFilter?.id) {
      modalCloseHandler();
    }
    setActiveFilters((prev) => {
      const updatedFilters = prev.filter((filter) => filter.id != id);
      // If this was the last filter, notify parent component
      if (updatedFilters.length === 0) {
        onFilterChange([]);
      }
      return updatedFilters;
    });
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: false};
        }
        return {...filter};
      });
    });
  };

  useEffect(() => {
    const mapedParentFilters = filters.map((data) => {
      return {
        id: data.projectFieldId,
        label: data.projectFieldLabel,
        type: data.dataType,
        isSelected: false,
      };
    });
    setParentFilters((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(mapedParentFilters)) {
        return mapedParentFilters;
      }
      return prev;
    });
  }, [filters]);

  const iconComponent = (
    <Box aria-hidden="true">
      <ChevronIcon className="expand-icon" aria-hidden="true" />
    </Box>
  );
  return (
    <Box
      className="doc-list-filter-accordion"
      data-test-id={'DocListFilterAccordion'}
    >
      <Accordion
        expanded={expanded}
        onChange={() =>
          setExpanded((prev) => {
            modalCloseHandler();
            return !prev;
          })
        }
        aria-label={title}
        onKeyDown={handleAccordionKeyDown}
      >
        <AccordionSummary
          expandIcon={iconComponent}
          aria-controls={`${title.replace(/\s+/g, '-').toLowerCase()}-content`}
          id={`${title.replace(/\s+/g, '-').toLowerCase()}-header`}
          tabIndex={0}
          role="button"
          aria-expanded={expanded}
        >
          <Box className="accordion-summary-content">
            <Box className="title-section">
              <Typography
                className="typo-text subhead4_size title"
                data-testid={`doc-list-filter-accordion-title`}
                tabIndex={0}
              >
                {title}
              </Typography>
              {/* Fix: set Typography to span to avoid <p> in <p> */}
              <Typography
                component="span"
                className="typo-text subhead4_size title"
                data-testid={`doc-list-filter-accordion-typography-text`}
              ></Typography>
              <ArrTooltipWrapper
                title={tooltipTitle}
                closeTooltipOnEscPress
                trigger={
                  <Box
                    component="div"
                    role="button"
                    tabIndex={0}
                    aria-label={t('docExplorer.filterAccordion.filterInfo')}
                    className="info-icon-button-wrapper"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                      }
                    }}
                    data-testid={`doc-list-filters-info`}
                  >
                    <InfoIcon className="info-icon" aria-hidden="true" />
                  </Box>
                }
              >
                {tooltipText}
              </ArrTooltipWrapper>
            </Box>

            <Box className="expand-icon-text-section">
              <Typography
                className=" typo-text buttonSmall_size  title"
                data-testid={`doc-list-filter-accordion-typography-expand/collapse`}
                tabIndex={0}
              >
                {expanded ? collapseText : expandText}
              </Typography>
              {/* Fix: set Typography to span to avoid <p> in <p> */}
              <Typography
                component="span"
                className="typo-text buttonSmall_size  title"
                data-testid={`doc-list-filter-accordion-typography-button`}
              ></Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="accordion-body">
            <Box
              className="parent-filter-container"
              aria-label={t('docExplorer.filterAccordion.availableFilters')}
            >
              <legend
                className="visually-hidden"
                data-testid={`doc-list-filter-accordion-available-filters`}
              >
                {t('docExplorer.filterAccordion.availableFilters')}
              </legend>
              {parentFilters.map((data) => (
                <DocListParentFilter
                  key={data.id}
                  filterData={data}
                  onClick={parentFilterClickHandler}
                  data-testid={`doc-list-filters-parent-${data.id}`}
                />
              ))}
            </Box>
            {activeFilters.length != 0 && (
              <Box className="sub-filter-container">
                <Divider className="filter-divider" />
                <Box
                  component="section"
                  className="active-filter-section"
                  aria-labelledby="active-filters-heading"
                  aria-label={t(
                    'docExplorer.filterAccordion.activeFiltersSection'
                  )}
                  data-testid="Active filters section"
                >
                  <Box className="active-filter-header-section">
                    <Typography
                      className="typo-text subhead4_size title"
                      component="h3"
                      id="active-filters-heading"
                      data-testid={`doc-list-filter-accordion-active-filters`}
                    >
                      {t('docExplorer.filterAccordion.activeFilters')}
                    </Typography>
                    {/* Fix: set Typography to span to avoid <p> in <p> */}
                    <Typography
                      component="span"
                      className="typo-text subhead4_size title"
                      data-testid={`doc-list-filter-accordion-typography-icon`}
                    ></Typography>
                    <Box className="active-filter-section-button">
                      <ArrButton
                        dataTestId="doclistClearAllHandlerButton"
                        aria-label={t('common.button.clearAll')}
                        size="small"
                        variant="text"
                        onClick={clearAllHandler}
                        onKeyDown={handleClearAllKeyDown}
                        tabIndex={0}
                      >
                        {t('common.button.clearAll')}
                      </ArrButton>
                      <ArrButton
                        dataTestId="doclistApplyFiltersHandlerButton"
                        aria-label={t(
                          'docExplorer.filterAccordion.applyFilters'
                        )}
                        size="small"
                        onClick={() => onFilterChange(activeFilters)}
                        onKeyDown={handleApplyFiltersKeyDown}
                        tabIndex={0}
                      >
                        {t('docExplorer.filterAccordion.applyFilters')}
                      </ArrButton>
                    </Box>
                  </Box>
                  <Box
                    component="ul"
                    className="active-filter-filter-section"
                    aria-label={t(
                      'docExplorer.filterAccordion.appliedFiltersList'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                  >
                    {getActiveFiltersChip()}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      <DocListFilterSelectionModal
        anchorElement={filterModalState.anchorEl}
        onClose={modalCloseHandler}
        filterSelectionData={
          filterModalState.selectedFilter ?? ({} as ActiveFilter)
        }
        onAddToFilters={onAddToFiltersHandler}
        closeOnEsc
      />
    </Box>
  );
};

export default DocListFilterAccordion;
